import { OnInit, Component, Input, OnDestroy, inject } from '@angular/core';
import { FormGroup, ControlContainer, FormGroupDirective } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { startWith, filter, map } from 'rxjs/operators';
import { nitrogenDeliveryRates } from 'src/app/shared/data';
import { SeasonType, CropData } from 'src/app/shared/models';

@Component({
  selector: 'app-green-manure-settings',
  templateUrl: './green-manure-settings.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  standalone: false,
})
export class GreenManureSettingsComponent implements OnInit, OnDestroy {
  parentFormGroup = inject(FormGroupDirective);

  @Input() greenManureFormGroup: FormGroup;
  @Input() crops: CropData[];

  readonly successRates = [50, 75, 100];
  seasonKeys: string[];
  filteredCrops$: Observable<CropData[]>;
  selectedCrop: CropData;

  private subscription = new Subscription();

  ngOnInit() {
    this.filteredCrops$ = this.greenManureFormGroup.controls.cropId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterCrops(value)),
    );
    this.seasonKeys = Object.keys(SeasonType);
    this.registerFormEvents();

    this.subscription.add(
      this.greenManureFormGroup.controls.cropId.valueChanges.subscribe((cropId: number) => {
        if (cropId) {
          const crop = this.findCrop(cropId);
          if (!crop) {
            this.greenManureFormGroup.controls.cropId.setValue(null, {
              emitModelToViewChange: false,
            });
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getCropId() {
    return this.greenManureFormGroup.get('cropId').value as number;
  }

  displayCrop() {
    return (cropId?: number) => (cropId ? this.findCrop(cropId)?.agrifirmCrop.name : '');
  }

  selectCrop(cropId: number) {
    this.selectedCrop = this.findCrop(cropId);
  }

  checkCrop() {
    if (!this.selectedCrop) {
      this.greenManureFormGroup.get('cropId').setValue(null);
    }
  }

  private filterCrops(value: string): CropData[] {
    const filterValue = value.toLowerCase();
    return this.crops.filter((option) =>
      option.agrifirmCrop.name.toLowerCase().includes(filterValue),
    );
  }

  private findCrop(cropId: number): CropData {
    return this.crops.find((crop) => crop.id === cropId);
  }

  private updateDefaultNitrogenDeliveryAmount() {
    const seasonGreenManureIncorporated = this.greenManureFormGroup.get(
      'seasonGreenManureIncorporated',
    ).value as SeasonType;
    const successRate = this.greenManureFormGroup.get('successRate').value as number;

    const cropId = this.greenManureFormGroup.get('cropId').value;
    if (
      typeof cropId === 'string' ||
      !(cropId as number) ||
      !seasonGreenManureIncorporated ||
      !successRate
    ) {
      return;
    }

    const crop = this.crops.find((c) => c.id === cropId);

    const nitrogenDeliveryRate = nitrogenDeliveryRates.find(
      (n) => n.isLeguminous === crop.isLeguminous && n.successRate === successRate,
    );

    const nitrogenDeliveryAmount =
      seasonGreenManureIncorporated === SeasonType.Spring
        ? nitrogenDeliveryRate.springNitrogenDeliveryAmount
        : nitrogenDeliveryRate.autumnNitrogenDeliveryAmount;

    this.greenManureFormGroup
      .get('nitrogenDeliveryAmount')
      .setValue(nitrogenDeliveryAmount, { emitEvent: false });
  }

  private setIsNitrogenDeliveryAmountSetManuallyValue(
    isNitrogenDeliveryAmountSetManually: boolean,
  ) {
    this.greenManureFormGroup
      .get('isNitrogenDeliveryAmountSetManually')
      .setValue(isNitrogenDeliveryAmountSetManually);
  }

  private registerFormEvents() {
    this.greenManureFormGroup.get('cropId').valueChanges.subscribe(() => {
      this.setIsNitrogenDeliveryAmountSetManuallyValue(false);
      this.setGreenManureDefaultValues();
      this.updateDefaultNitrogenDeliveryAmount();
    });

    this.greenManureFormGroup.get('seasonGreenManureIncorporated').valueChanges.subscribe(() => {
      this.setIsNitrogenDeliveryAmountSetManuallyValue(false);
      this.updateDefaultNitrogenDeliveryAmount();
    });

    this.greenManureFormGroup.get('successRate').valueChanges.subscribe(() => {
      this.setIsNitrogenDeliveryAmountSetManuallyValue(false);
      this.updateDefaultNitrogenDeliveryAmount();
    });

    this.greenManureFormGroup
      .get('isNitrogenDeliveryAmountSetManually')
      .valueChanges.subscribe((isNitrogenDeliveryAmountSetManually) => {
        if (!isNitrogenDeliveryAmountSetManually) {
          this.updateDefaultNitrogenDeliveryAmount();
        }
      });

    this.greenManureFormGroup
      .get('nitrogenDeliveryAmount')
      .valueChanges.subscribe(() => this.setIsNitrogenDeliveryAmountSetManuallyValue(true));
  }

  private setGreenManureDefaultValues() {
    const seasonCurrentValue = this.greenManureFormGroup.get('seasonGreenManureIncorporated')
      .value as SeasonType;
    const successRateCurrentValue = this.greenManureFormGroup.get('successRate').value as number;

    this.greenManureFormGroup
      .get('seasonGreenManureIncorporated')
      .setValue(seasonCurrentValue ?? SeasonType.Autumn);
    this.greenManureFormGroup.get('successRate').setValue(successRateCurrentValue ?? 100);
  }
}
